"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateSchemaFromHTML = exports.extractSchemaFromHTML = void 0;
exports.filterSchemaAndHtmlByQuestions = filterSchemaAndHtmlByQuestions;
// Generate a random ID
const generateId = () => {
    return `input_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
// Extract validation rules from HTML attributes
const extractValidations = (element) => {
    const validations = [];
    // Required validation
    if (element.hasAttribute("required")) {
        validations.push({
            type: "required",
            message: "This field is required",
        });
    }
    // Email validation
    if (element.getAttribute("type") === "email") {
        validations.push({
            type: "email",
            message: "Please enter a valid email address",
        });
    }
    // Min/Max length
    const minLength = element.getAttribute("minlength");
    if (minLength) {
        validations.push({
            type: "minLength",
            value: parseInt(minLength),
            message: `Minimum length is ${minLength} characters`,
        });
    }
    const maxLength = element.getAttribute("maxlength");
    if (maxLength) {
        validations.push({
            type: "maxLength",
            value: parseInt(maxLength),
            message: `Maximum length is ${maxLength} characters`,
        });
    }
    // Pattern validation
    const pattern = element.getAttribute("pattern");
    if (pattern) {
        validations.push({
            type: "pattern",
            value: pattern,
            message: "Please match the requested format",
        });
    }
    return validations;
};
// Extract label for an input element
const extractLabel = (element) => {
    var _a, _b;
    // Try to find associated label
    const id = element.getAttribute("id");
    if (id) {
        const label = document.querySelector(`label[for="${id}"]`);
        if (label) {
            return ((_a = label.textContent) === null || _a === void 0 ? void 0 : _a.trim()) || "";
        }
    }
    // Try to find parent label
    const parentLabel = element.closest("label");
    if (parentLabel) {
        return ((_b = parentLabel.textContent) === null || _b === void 0 ? void 0 : _b.trim()) || "";
    }
    // Try to find nearby label or placeholder
    const placeholder = element.getAttribute("placeholder");
    if (placeholder) {
        return placeholder.charAt(0).toUpperCase() + placeholder.slice(1);
    }
    // Try to find name attribute
    const name = element.getAttribute("name");
    if (name) {
        return name.charAt(0).toUpperCase() + name.slice(1).replace(/[-_]/g, " ");
    }
    return "Untitled Field";
};
// Extract options for select, radio, checkbox elements
const extractOptions = (element) => {
    const options = [];
    if (element.tagName === "SELECT") {
        const optionElements = element.querySelectorAll("option");
        optionElements.forEach((option) => {
            options.push({
                value: option.getAttribute("value") || option.textContent || "",
                label: option.textContent || "",
            });
        });
    }
    else if (element.tagName === "INPUT" &&
        (element.getAttribute("type") === "radio" ||
            element.getAttribute("type") === "checkbox")) {
        // For radio/checkbox groups, we need to find all related inputs
        const name = element.getAttribute("name");
        if (name) {
            const relatedInputs = document.querySelectorAll(`input[name="${name}"]`);
            relatedInputs.forEach((input) => {
                const value = input.getAttribute("value") || "";
                const label = extractLabel(input);
                options.push({ value, label });
            });
        }
    }
    return options;
};
// Extract all HTML attributes
const extractAttributes = (element) => {
    const attributes = {};
    const attrs = element.attributes;
    for (let i = 0; i < attrs.length; i++) {
        const attr = attrs[i];
        attributes[attr.name] = attr.value;
    }
    return attributes;
};
// Extract form inputs from HTML
const extractFormInputs = (html) => {
    const inputs = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    // Find all form elements
    const formElements = doc.querySelectorAll("input, textarea, select");
    let order = 0;
    formElements.forEach((element) => {
        const htmlElement = element;
        const tagName = htmlElement.tagName.toLowerCase();
        const type = htmlElement.getAttribute("type") || tagName;
        // Skip hidden inputs and buttons
        if (type === "hidden" ||
            type === "submit" ||
            type === "button" ||
            type === "reset") {
            return;
        }
        const validations = extractValidations(htmlElement);
        const isRequired = validations.some((v) => v.type === "required");
        const options = extractOptions(htmlElement);
        const attributes = extractAttributes(htmlElement);
        const formInput = {
            id: generateId(),
            type: type,
            name: htmlElement.getAttribute("name") || `field_${order}`,
            label: extractLabel(htmlElement),
            placeholder: htmlElement.getAttribute("placeholder") || undefined,
            value: htmlElement.getAttribute("value") || undefined,
            isRequired,
            validations,
            attributes,
            options: options.length > 0 ? options : undefined,
            order: order++,
        };
        inputs.push(formInput);
    });
    return inputs;
};
// Main schema extraction function
const extractSchemaFromHTML = (html, metadata) => {
    try {
        console.log("🔍 Extracting schema from HTML...");
        // Create a temporary DOM to parse the HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        document.body.appendChild(tempDiv);
        const inputs = extractFormInputs(html);
        // Clean up
        document.body.removeChild(tempDiv);
        console.log("📋 Extracted inputs:", inputs);
        const schema = {
            id: `schema_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: metadata.name || "Generated Form",
            description: metadata.description,
            inputs,
            metadata: {
                createdAt: new Date(),
                updatedAt: new Date(),
                generatedFrom: {
                    imageUrl: metadata.imageUrl,
                    prompt: metadata.prompt,
                    userId: metadata.userId,
                },
                htmlCode: html,
            },
        };
        return {
            success: true,
            schema,
            warnings: inputs.length === 0 ? ["No form inputs found in the HTML"] : undefined,
        };
    }
    catch (error) {
        console.error("❌ Error extracting schema:", error);
        return {
            success: false,
            errors: [`Failed to extract schema: ${error}`],
        };
    }
};
exports.extractSchemaFromHTML = extractSchemaFromHTML;
// Update schema with new HTML
const updateSchemaFromHTML = (existingSchema, newHtml) => {
    try {
        const newSchema = (0, exports.extractSchemaFromHTML)(newHtml, {
            name: existingSchema.name,
            description: existingSchema.description,
            imageUrl: existingSchema.metadata.generatedFrom.imageUrl,
            prompt: existingSchema.metadata.generatedFrom.prompt,
            userId: existingSchema.metadata.generatedFrom.userId,
        });
        if (newSchema.success && newSchema.schema) {
            newSchema.schema.metadata.updatedAt = new Date();
            newSchema.schema.metadata.htmlCode = newHtml;
        }
        return newSchema;
    }
    catch (error) {
        console.error("❌ Error updating schema:", error);
        return {
            success: false,
            errors: [`Failed to update schema: ${error}`],
        };
    }
};
exports.updateSchemaFromHTML = updateSchemaFromHTML;
function stripHtml(html) {
    if (!html)
        return "";
    return html.replace(/<[^>]*>/g, "").trim();
}
function getPreservedHtml(doc, originalHtml) {
    if (/\<html[\s\S]*\<\/html\>/i.test(originalHtml)) {
        return doc.documentElement.outerHTML;
    }
    return doc.body.innerHTML;
}
function filterSchemaAndHtmlByQuestions(schema, html, formQuestions) {
    const hasQuestions = Array.isArray(formQuestions) && formQuestions.length > 0;
    // Normalize questions
    const questions = hasQuestions
        ? formQuestions.map((q) => ({
            _id: q._id,
            question: stripHtml(q.question).toLowerCase(),
            uniqueKey: q.uniqueKey,
        }))
        : [];
    const inputIdMap = {};
    const matchedInputNames = new Set();
    // Update schema inputs with formInputId using fuzzy matching
    const updatedInputs = schema.inputs
        .map((input) => {
        if (!hasQuestions)
            return Object.assign(Object.assign({}, input), { formInputId: undefined });
        const selectedQuestion = questions.find((q) => {
            var _a;
            return `key_${input.name.toLowerCase()}` === ((_a = q.uniqueKey) === null || _a === void 0 ? void 0 : _a.toLowerCase());
        });
        if (selectedQuestion) {
            inputIdMap[input.name] = selectedQuestion._id;
            matchedInputNames.add(input.name);
            return Object.assign(Object.assign({}, input), { formInputId: selectedQuestion._id, formInput: selectedQuestion });
        }
        else {
            return undefined;
        }
        // return { ...input, formInputId: undefined } as FormInput;
    })
        .filter((el) => !!el);
    let updatedHtml = html;
    try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const form = doc.querySelector("form") || doc.body;
        // Update all existing inputs outside form-group
        const allInputs = doc.querySelectorAll("input, textarea, select");
        allInputs.forEach((el) => {
            const originalName = el.name;
            if (!originalName)
                return;
            const matchedInput = updatedInputs.find((input) => input.name === originalName);
            if (matchedInput === null || matchedInput === void 0 ? void 0 : matchedInput.formInputId) {
                el.name = matchedInput.formInputId;
                if ("id" in el)
                    el.id = matchedInput.formInputId;
            }
        });
        // Helper to create input element
        function createInputElement(input) {
            var _a, _b;
            let el;
            if (input.type === "textarea") {
                el = doc.createElement("textarea");
                if (input.value)
                    el.value = input.value;
            }
            else if (input.type === "select") {
                el = doc.createElement("select");
                (_a = input.options) === null || _a === void 0 ? void 0 : _a.filter((opt) => !opt.isHidden).forEach((opt) => {
                    const option = doc.createElement("option");
                    option.value = opt.value;
                    option.textContent = opt.label;
                    el.appendChild(option);
                });
            }
            else if (input.type === "radio" || input.type === "checkbox") {
                el = doc.createElement("div");
                (_b = input.options) === null || _b === void 0 ? void 0 : _b.filter((opt) => !opt.isHidden).forEach((opt) => {
                    const wrapper = doc.createElement("div");
                    const inp = doc.createElement("input");
                    inp.type = input.type;
                    inp.name = input.formInputId || input.name;
                    inp.value = opt.value;
                    Object.entries(input.attributes || {}).forEach(([k, v]) => inp.setAttribute(k, v));
                    const lbl = doc.createElement("label");
                    lbl.textContent = opt.label;
                    wrapper.appendChild(inp);
                    wrapper.appendChild(lbl);
                    el.appendChild(wrapper);
                });
            }
            else {
                el = doc.createElement("input");
                el.type = input.type;
                if (input.value)
                    el.value = input.value;
            }
            // Common attributes
            Object.entries(input.attributes || {}).forEach(([k, v]) => el.setAttribute(k, v));
            if ("name" in el && input.formInputId)
                el.name = input.formInputId;
            if ("id" in el && input.formInputId)
                el.id = input.formInputId;
            if (input.placeholder && "placeholder" in el)
                el.setAttribute("placeholder", input.placeholder);
            if (input.isRequired)
                el.setAttribute("required", "true");
            return el;
        }
        const allFormGroups = Array.from(doc.querySelectorAll("div.form-group"));
        updatedInputs.forEach((input, index) => {
            let targetFormGroup = null;
            if (input.formInputId) {
                targetFormGroup =
                    allFormGroups.find((group) => group.getAttribute("data-input-id") === input.formInputId ||
                        group.id === input.formInputId) || null;
            }
            if (input.isHidden) {
                targetFormGroup === null || targetFormGroup === void 0 ? void 0 : targetFormGroup.remove();
                return;
            }
            if (!targetFormGroup && allFormGroups[index])
                targetFormGroup = allFormGroups[index];
            if (!targetFormGroup) {
                targetFormGroup = doc.createElement("div");
                targetFormGroup.className = "form-group";
                form.appendChild(targetFormGroup);
                allFormGroups.push(targetFormGroup);
            }
            if (targetFormGroup) {
                // Clear only input/textarea/select, keep labels/buttons
                const existingInputs = targetFormGroup.querySelectorAll("input, textarea, select");
                existingInputs.forEach((el) => el.remove());
                // Set ids
                targetFormGroup.id = input.formInputId || input.name;
                if (input.formInputId)
                    targetFormGroup.setAttribute("data-input-id", input.formInputId);
                // Label
                let labelEl = targetFormGroup.querySelector("label");
                if (input.label) {
                    if (!labelEl) {
                        labelEl = doc.createElement("label");
                        targetFormGroup.insertBefore(labelEl, targetFormGroup.firstChild);
                    }
                    labelEl.setAttribute("for", input.formInputId || input.name);
                    labelEl.textContent = input.label;
                }
                else if (labelEl)
                    labelEl.remove();
                // Append input
                const inputEl = createInputElement(input);
                targetFormGroup.appendChild(inputEl);
            }
        });
        // Remove groups that don't match any question
        allFormGroups.forEach((group) => {
            if (!hasQuestions)
                return; // keep all groups for new forms
            const hasMatch = updatedInputs.some((input) => input.formInputId && input.formInputId === group.id);
            if (!hasMatch) {
                group.remove(); // remove the entire form group from DOM
            }
        });
        // Ensure submit button is always last
        const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submitButton)
            form.appendChild(submitButton);
        updatedHtml = getPreservedHtml(doc, html);
    }
    catch (e) {
        console.error("Error parsing HTML:", e);
    }
    const updatedSchema = Object.assign(Object.assign({}, schema), { inputs: updatedInputs });
    return { filteredSchema: updatedSchema, updatedHtml };
}
